import { when } from 'mobx'
import { observer } from 'mobx-react-lite'
import { WebMCPProvider, useMcpTool } from 'webmcp-react'
import { useWebMcpEnabled } from 'entities/WebMcp/lib/flag'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'
import {
  setBroadcastAudienceSchema,
  setBroadcastDetailsSchema,
  setBroadcastSenderSchema,
} from 'entities/WebMcp/model/schemas'
import { useBroadcastViewContext } from 'pages/broadcasts/ui/BroadcastView/context/broadcastViewContext'
import { applyBroadcastSender } from './applyBroadcastSender'

// Setters are only safe once the form's async init has finished (store.loading -> false).
const READY_TIMEOUT = 10000

const BroadcastFillToolsInner = () => {
  const store = useBroadcastViewContext()

  useMcpTool({
    name: 'set_broadcast_details',
    description:
      "Fill the open broadcast form's name and/or message. Only available while the broadcast create or edit form is open. Populates the form for the user to review and send. Needs the user's approval before it runs. Call it in the same reply as the other set_broadcast_* tools you need, so the whole broadcast is one approval card rather than one per field.",
    input: setBroadcastDetailsSchema,
    handler: async ({ name, message }) => {
      try {
        await when(() => !store.loading, { timeout: READY_TIMEOUT })
        if (name !== undefined) store.setName(name)
        if (message !== undefined) {
          const messageFieldStore = store.broadcastSMSStore.messageFieldStore
          // isReplace overwrites the field; the default (append) would double up
          // the text on an edit. Preserve any attachments the user already added.
          messageFieldStore.setMessageData({
            message,
            attachments: messageFieldStore.attachments,
            isReplace: true,
            isFocus: false,
          })
        }
        return okResult({ set: { name: name ?? null, message: message ?? null } })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'set_broadcast_audience',
    description:
      "Set the open broadcast form's audience by tag ids and/or explicit contact ids. Resolve tag names to ids with list_tags first. Only available while the broadcast form is open. Needs the user's approval before it runs. Call it in the same reply as the other set_broadcast_* tools you need, so the whole broadcast is one approval card rather than one per field.",
    input: setBroadcastAudienceSchema,
    handler: async ({ tag_ids, contact_ids }) => {
      try {
        await when(() => !store.loading, { timeout: READY_TIMEOUT })
        const contacts = store.broadcastContactsStore
        const tags = tag_ids ?? []
        const contactIds = contact_ids ?? []
        // setInitStoredFiltersListGroups syncs the filters AND recomputes the recipient
        // count. Plain syncFiltersListGroups leaves total stale (often 0), so the form's
        // send-validation shows "Please select the contacts" despite applied ids.
        await contacts.setInitStoredFiltersListGroups({
          tags: tags,
          contacts: contactIds,
          segments: [],
          advanced: [],
        })
        // An empty audience is a valid API result but a dead-end broadcast: the
        // form fills yet nothing can send. Report it explicitly so the agent tells
        // the user rather than leaving them with a silently empty broadcast.
        return okResult(
          {
            applied: { tag_ids: tags, contact_ids: contactIds },
            recipient_count: contacts.total,
          },
          contacts.total === 0
            ? 'These filters match 0 contacts, so this broadcast would reach no one and cannot send. Tell the user, and suggest a different tag or adding contacts.'
            : undefined
        )
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'set_broadcast_sender',
    description:
      "Set the open broadcast form's sending number. Use a number_id from list_broadcast_numbers. Only available while the broadcast form is open. Needs the user's approval before it runs. Settle the number with the user BEFORE you fill anything else, then call this in the same reply as the other set_broadcast_* tools, so the whole broadcast is one approval card rather than one per field.",
    input: setBroadcastSenderSchema,
    handler: async ({ number_id }) => {
      try {
        const result = await applyBroadcastSender(store, number_id, READY_TIMEOUT)
        if (!result.ok) return errorResult(new Error(result.reason))
        return okResult({ sender: { number_id } })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}

/**
 * Page-scoped WebMCP fill tools bound to the live BroadcastViewStore. Mounted
 * with the form, so the tools are advertised only while the form is open and
 * auto-unregister on unmount. Gated by the agent flag; the inner WebMCPProvider
 * matches the global one so useMcpTool registers on the same navigator.modelContext.
 */
export const BroadcastFillTools = observer(() => {
  const enabled = useWebMcpEnabled()
  if (!enabled) return null
  return (
    <WebMCPProvider name='salesmsg-broadcast-form' version='0.1.0'>
      <BroadcastFillToolsInner />
    </WebMCPProvider>
  )
})
